/**
 * Quantities on a return, done exactly.
 *
 * <p>Quantities cross the wire as decimal strings with three places ("1.000", "2.500") because the
 * platform sells by weight and length as well as by the each. Arithmetic is done in thousandths as
 * integers, so taking 0.5 from 1.5 leaves exactly 1.000 and never 0.9999999.
 */

const SCALE = 3;

/** Thousandths, or null for something that is not a non-negative decimal. */
export function toMilli(quantity: string | null | undefined): bigint | null {
  if (quantity == null) return null;
  const match = /^(\d+)(?:\.(\d*))?$/.exec(quantity.trim());
  if (!match) return null;
  const whole = match[1] ?? '0';
  const fraction = (match[2] ?? '').slice(0, SCALE).padEnd(SCALE, '0');
  return BigInt(whole) * 1000n + BigInt(fraction);
}

export function fromMilli(value: bigint): string {
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const whole = abs / 1000n;
  const fraction = (abs % 1000n).toString().padStart(SCALE, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

/** `a - b`, never below zero. */
export function subtractQuantity(a: string | null | undefined, b: string | null | undefined): string {
  const left = (toMilli(a) ?? 0n) - (toMilli(b) ?? 0n);
  return fromMilli(left > 0n ? left : 0n);
}

export function isPositive(quantity: string | null | undefined): boolean {
  const milli = toMilli(quantity);
  return milli != null && milli > 0n;
}

/**
 * The choices for a whole-item quantity picker — 1 up to `max` — or null when `max` is fractional
 * or too large for a list, and a number field suits better.
 */
export function wholeChoices(max: string | null | undefined, limit = 50): number[] | null {
  const milli = toMilli(max);
  if (milli == null || milli <= 0n || milli % 1000n !== 0n) return null;
  const count = Number(milli / 1000n);
  if (count > limit) return null;
  return Array.from({ length: count }, (_, index) => index + 1);
}

/** Trims the scale off for display: "2.000" reads as "2", "1.500" as "1.5". */
export function displayQuantity(quantity: string | null | undefined): string {
  if (quantity == null) return '';
  const milli = toMilli(quantity);
  if (milli == null) return quantity;
  return fromMilli(milli).replace(/\.?0+$/, '');
}
